import { useMemo, useState } from "react";
import { format } from "date-fns";
import { Eye, Plus } from "lucide-react";

import { Button } from "components/ui/Button";
import { DataTable } from "components/ui/DataTable";
import { Dialog } from "components/ui/Dialog";
import { PatientNameLink } from "features/communications/components/PatientNameLink/PatientNameLink";

import s from "./PatientSms.module.css";

const ROWS_OPTIONS = [10, 20, 50];

const formatDate = (value) => {
  if (!value) return "-";
  try {
    return format(new Date(value), "dd MMM yyyy, HH:mm");
  } catch {
    return value;
  }
};

const DATE_COLUMN = {
  id: "created",
  header: "Date Sent",
  width: "160px",
  render: (sms) => formatDate(sms.created),
};

const PATIENT_COLUMN = {
  id: "patient",
  header: "Patient",
  width: "120px",
  flexGrow: 1,
  render: (sms) => (
    <PatientNameLink patientId={sms.patientId} patient={sms.patient} />
  ),
};

const MOBILE_COLUMN = {
  id: "mobile",
  header: "Mobile",
  width: "130px",
  render: (sms) => sms.mobile || "-",
};

const MESSAGE_COLUMN = {
  id: "message",
  header: "Message",
  // Truncated to one line rather than wrapped: an appointment SMS carries a join
  // link long enough to push the table into a horizontal scroll at 1920, and the
  // full text is a click away in the preview.
  width: "200px",
  flexGrow: 1,
  render: (sms) => (
    <span className={s.messageText} title={sms.message || ""}>
      {sms.message || "-"}
    </span>
  ),
};

/*
 * `handleClick` and `showPatientColumn` are what separate the patient's SMS tab from the
 * practice-wide one: with no patient in context there is nobody to send to, so the Send
 * button is left out, and the patient each message went to becomes a column instead.
 */
const PatientsSmsTable = ({
  smsList,
  handleClick,
  isLoading = false,
  showPatientColumn = false,
  emptyText = "Messages sent to this patient will appear here.",
}) => {
  const [rows, setRows] = useState(10);
  const [search, setSearch] = useState("");
  const [previewedSms, setPreviewedSms] = useState(null);

  const columns = useMemo(
    () =>
      [
        DATE_COLUMN,
        showPatientColumn && PATIENT_COLUMN,
        MOBILE_COLUMN,
        MESSAGE_COLUMN,
      ].filter(Boolean),
    [showPatientColumn]
  );

  const filtered = useMemo(() => {
    if (!search.trim()) return smsList;
    const lower = search.toLowerCase();

    return smsList.filter((sms) =>
      (sms.message || "").toLowerCase().includes(lower)
    );
  }, [smsList, search]);

  const rowActions = useMemo(
    () => [
      {
        label: "View",
        icon: Eye,
        primary: true,
        onClick: (sms) => setPreviewedSms(sms),
      },
    ],
    []
  );

  return (
    <div className={s.wrapper}>
      <div className={s.header}>
        {/* Named for the tab rather than built from the patient's name: the heading
            rendered before usePatientData resolved, so it read "Messages sent to
            undefined undefined" on every load. The patient is already named at the top
            of the page. */}
        <h3 className={s.title}>SMS</h3>
        {!!handleClick && (
          <Button variant="text" onClick={handleClick} endIcon={Plus}>
            Send SMS
          </Button>
        )}
      </div>

      <DataTable
        columns={columns}
        data={filtered}
        getRowId={(sms, index) => sms.id ?? index}
        isLoading={isLoading}
        noScroll
        rowActions={rowActions}
        emptyTitle={search ? "No messages found" : "No messages sent"}
        emptyText={
          search ? "No SMS messages match your search." : emptyText
        }
        rowsValue={rows}
        onRowsChange={(value) => setRows(value)}
        rowsOptions={ROWS_OPTIONS}
        search={{
          value: search,
          onChange: setSearch,
          placeholder: "Search messages...",
        }}
      />

      <Dialog
        open={!!previewedSms}
        onClose={() => setPreviewedSms(null)}
        title="SMS"
        width={640}
      >
        <div className={s.preview}>
          <div className={s.previewMeta}>
            <span>{formatDate(previewedSms?.created)}</span>
            <span>{previewedSms?.mobile || "-"}</span>
          </div>
          <p className={s.previewMessage}>{previewedSms?.message || "-"}</p>
        </div>
      </Dialog>
    </div>
  );
};

export { PatientsSmsTable };
